import { Fund, Schedule } from "source/giving/donate/form/types"

const givingProfilePath = "/giving/profile"

export const ProfileNavItems = [
  { label: "Overview", path: givingProfilePath },
  {
    label: "Donation history",
    path: `${givingProfilePath}/donations`,
  },
  {
    label: "Planned giving",
    path: `${givingProfilePath}/planned`,
  },
  {
    label: "Payment methods",
    path: `${givingProfilePath}/payment-methods`,
  },
  {
    label: "Text2Give",
    path: `${givingProfilePath}/text-to-give`,
  },
  {
    label: "Notifications",
    path: `${givingProfilePath}/notifications`,
  },
  {
    label: "Statements",
    path: `${givingProfilePath}/statements`,
  },
] as const

export type ProfileNav = (typeof ProfileNavItems)[number]

export type SendsReceiptsValue = "always" | "never"

export type SendsReceiptsOption = {
  label: string
  value: SendsReceiptsValue
}

export type GivingPerson = {
  can_view_text2give_settings: boolean
  email_addresses: string[]
  first_name: string
  giving_preferred_email_address: string | null
  last_name: string
  sends_receipts: SendsReceiptsValue
}

export type Organization = {
  id: string
  name: string
  text2give_enabled: boolean
  text2give_phone_number: string | null
  credit_cards_allowed: boolean
}

export type Text2GivePhoneNumber = {
  id: string
  phone_number: string
  payment_method_id: string | null
}

export type PaymentMethod = {
  id: string
  brand: string
  last4: string
  method_type: string
}

export type Statement = {
  id: string
  amount_cents: number
  amount_currency: string
  created_at: string
  starts_on: string
  ends_on: string
  donation_count: number
  download_url: string
  name: string
}

export type RecurringDonationsResponse = {
  data: RecurringDonationResource[]
  included: RecurringDonationsResourceIncludedData[]
}

export type RecurringDonationResponse = {
  data: RecurringDonationResource
  included: RecurringDonationsResourceIncludedData[]
}

export type RecurringDonationResource = {
  id: string
  type: "RecurringDonation"
  attributes: Omit<RecurringDonation, "id">
  relationships: {
    payment_method: { data: { type: "PaymentMethod"; id: string } | null }
    designations: {
      data: { type: "RecurringDonationDesignation"; id: string }[]
    }
  }
}

type RecurringDonationsResourceIncludedData =
  | RecurringDonationDesignationResource
  | FundResource

export function isDesignation(
  item: RecurringDonationsResourceIncludedData,
): item is RecurringDonationDesignationResource {
  return item.type === "RecurringDonationDesignation"
}

export function isFund(
  item: RecurringDonationsResourceIncludedData | PledgeResourceIncludedData,
): item is FundResource {
  return item.type === "Fund"
}

export type RecurringDonationDesignationResource = {
  type: "RecurringDonationDesignation"
  id: string
  attributes: { amount_cents: number }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type RecurringDonationDesignationPayload = {
  id: string
  type: "RecurringDonationDesignation"
  attributes: {
    amount: string
  }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type NewRecurringDonationDesignationPayload = {
  type: "RecurringDonationDesignation"
  attributes: {
    amount: string
  }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type FundResource = {
  type: "Fund"
  id: string
  attributes: Omit<Fund, "id">
}

export type RecurringDonation = {
  id: string
  awaiting_verification: boolean
  designations: Designation[]
  fee_covered: boolean
  humanized_schedule: string
  last_deterministically_processed_at: string | null
  next_occurrence: string | null
  payment_method_id: string | null
  release_hold_at: string | null
  on_hold_indefinitely: boolean
  schedule: Schedule
  updated_at: string
}

export type RecurringDonationStatus =
  | "active"
  | "indefinite_hold"
  | "hold_until"

export type Designation = {
  id: string | null
  fund: Fund
  amount: string
}

export type PledgesResource = {
  data: PledgeResource[]
  included: PledgeResourceIncludedData[]
}

export type PledgeResource = {
  id: string
  type: "Pledge"
  attributes: Omit<Pledge, "id" | "joint_giver" | "fund" | "pledge_campaign">
  relationships: {
    pledge_campaign: { data: { type: "PledgeCampaign"; id: string } }
    joint_giver: { data: { type: "Person"; id: string } | null }
    fund: { data: { type: "Fund"; id: string } }
  }
}

type PledgeResourceIncludedData =
  | GivingPersonResource
  | PledgeCampaignResource
  | FundResource

export function isGivingPerson(
  item: PledgeResourceIncludedData,
): item is GivingPersonResource {
  return item.type === "Person"
}

export function isPledgeCampaign(
  item: PledgeResourceIncludedData,
): item is PledgeCampaignResource {
  return item.type === "PledgeCampaign"
}

export type GivingPersonResource = {
  type: "Person"
  attributes: GivingPerson
}

export type PledgeCampaignResource = {
  type: "PledgeCampaign"
  id: string
  attributes: Omit<PledgeCampaign, "id" | "fund">
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type Pledge = {
  id: string
  amount_cents: number
  amount_currency: string
  fund: Fund
  joint_amount_cents: number
  joint_donated_total_cents: number
  joint_giver_amount_cents: number | null
  joint_giver: GivingPerson | null
  joint_progress_percent: number
  pledge_campaign: PledgeCampaign
}

export type PledgeCampaign = {
  id: string
  description: string | null
  ends_at: string
  fund: Fund
  goal_cents: number | null
  goal_currency: string
  name: string
  received_of_goal_percent: string | null
  received_total_cents: number | null
  show_goal_in_church_center_app: boolean
  starts_at: string
}
